import { COMPONENT_TYPE } from './component_type';
import {
  AggregatedField,
  ExpressionField,
  Field,
  GroupedField,
  Relation,
  WindowedField,
} from '../package-designer/package.models';
import { AnyConnection, ConnectionTypeName } from '../connections/connection.models';
import { Package } from '../packages/package.models';

export enum AUTHENTICATION_TYPES {
  NONE = 'none',
  BASIC = 'basic',
  CONNECTION = 'connection',
}

export enum COMPONENT_CATEGORY {
  source = 'source',
  transformation = 'transformation',
  destination = 'destination',
  workflow = 'workflow',
  note = 'note',
}

export interface ComponentTypeItem {
  id?: string;
  alias?: string;
  xy?: number[];
  package_id?: number;
  originalName: string;
  description?: string;
  noteHeight?: number;
  noteWidth?: number;
  editorDirective?: string;
  type: COMPONENT_CATEGORY;
  connectionTypes?: string;
  dynamic_connection?: string;
  icon: string;
  componentType: COMPONENT_TYPE;
  message?: string;
  connection?: AnyConnection;
  package?: Package;
  isHovered?: boolean;
  isSelected?: boolean;
  object_name?: string;
  predicates?: {
    expressions?: ExpressionField[];
    match_type?: string;
  };
  extraFields?: [
    {
      name: string;
      type: string;
    },
  ];
  record_settings?: {
    field_delimiter: string;
    text_qualifier: string;
    skip_header: boolean;
    escape_character: string;
  };
  relations?: Relation[];
  percentage?: number;
  partitioned_fields?: Field[];
  ordered_fields?: Field[];
  column_mappings?: Field[];
  windowed_fields?: WindowedField[];
  grouped_fields?: GroupedField[];
  aggregated_fields?: AggregatedField[];
  fields?: Field[];
  partitioning?: string;
  order?: string;
  grouping_type?: string;
  n?: number;
  field_alias?: string;
  dense?: boolean;
  data_order?: string;
  name?: string;
  inputMin: number;
  inputMax: number;
  outputMin?: number;
  outputMax?: number;
  disableSchemaLoad?: boolean;
  hasDataPreviewRefreshButton?: boolean;
  hasDataPreview?: boolean;
  record_delimiter?: string;
  record_type?: string;
  access_mode?: string;
  customer_id_field_alias?: string;
  account_name_field_alias?: string;
  property_name_field_alias?: string;
  profile_name_field_alias?: string;
  profile_id_field_alias?: string;
  response_type?: string;
  report_aggregation_type?: string;
  time_period_date_field_alias?: string;
  time_period_hour_field_alias?: string;
  hasTimePeriodField?: boolean;
  join_type?: string;
  optimization_type?: string;
  destination_type?: {
    csv_destination_type?: {
      field_delimiter?: string;
      text_qualifier?: string;
      write_header?: boolean;
      escape_character?: string;
      line_ending?: string;
    };
  };
  defaults?: {
    use_legacy_sql?: boolean;
    stored_procedure?: boolean;
    where_clause?: string;
    api_version?: string;
    level?: string;
    report_date_range_type?: string;
    report_date_range_min?: string;
    report_date_range_max?: string;
    manual_breakdown?: string;
    time_increment?: string;
    filtering?: string;
    action_report_time?: string;
    report_on?: string;
    ad_accounts?: string[];
    with_custom_fields?: boolean;
    report_type?: string;
    customer_ids?: string;
    customer_id_field_alias?: string;
    profile_ids?: string;
    account_name_field_alias?: string;
    property_name_field_alias?: string;
    property_id_field_alias?: string;
    account_id_field_alias?: string;
    profile_name_field_alias?: string;
    profile_id_field_alias?: string;
    segment?: string;
    filters?: any;
    sort?: any;
    include_empty_rows?: boolean;
    report_sampling_level?: string;
    method?: string;
    response_type?: string;
    pagination_scheme?: string;
    use_pagination?: boolean;
    pagination_sleep_interval?: number;
    json_path?: string;
    authentication?: AUTHENTICATION_TYPES;
    object_name?: string;
    source_action?: string;
    access_mode?: string;
    record_delimiter?: string;
    record_type?: string;
    record_settings?: {
      field_delimiter: string;
      text_qualifier: string;
      skip_header: boolean;
      escape_character: string;
    };
    query?: string;
    accounts_campaigns?: string;
    columns?: string[];
    report_aggregation_type?: string;
    time_zone?: string;
    partitioned_fields?: string[];
    ordered_fields?: string[];
    windowed_fields?: string[];
    percentage?: string;
    join_type?: string;
    optimization_type?: string;
    relations?: string[];
    before_action?: string;
    predicates?: {
      match_type?: string;
      expressions?: string[];
    };
    message?: string;
    grouping_type?: string;
    grouped_fields?: string[];
    aggregated_fields?: string[];
    create_table?: boolean;
    add_columns?: boolean;
    operation_type?: string;
    intermediate_compression_type?: string;
    max_errors?: number;
    track_errors?: boolean;
    enable_null_value?: boolean;
    errors_output_path?: string;
    account_id?: string;
    truncate_columns?: boolean;
    trim_blanks?: boolean;
    empty_as_null?: boolean;
    blank_as_null?: boolean;
    null_string?: string;
    replacement_char?: string;
    round_decimals?: boolean;
    explicit_ids?: boolean;
    comp_update?: string;
    comp_rows?: number;
    max_bad_records?: number;
    batch_size?: number;
    parallel?: number;
    single_transaction_per_batch?: boolean;
    identity_insert_sql_server?: boolean;
    bucket?: string;
    path?: string;
    file_names?: string;
    prefix?: string;
    suffix?: any;
    destination_type?: {
      csv_destination_type?: {
        field_delimiter?: string;
        text_qualifier?: string;
        write_header?: boolean;
        escape_character?: string;
        line_ending?: string;
      };
    };
    compression_type?: string;
    enforce_single_file?: boolean;
    warehouse_name?: string;
    database_name?: string;
    id_field?: string;
    column_mappings?: string[];
    chunk_size?: number;
    operator?: string;
  };
}

export const COMPONENT_TYPES: ComponentTypeItem[] = [
  {
    componentType: COMPONENT_TYPE.STICKY_NOTE_COMPONENT,
    originalName: 'Note',
    inputMin: 0,
    inputMax: 0,
    icon: 'icon-sticky-note',
    type: COMPONENT_CATEGORY.note,
  },
  {
    originalName: 'File Storage',
    description:
      'Use the file storage source component to define the file data source for your data flow: which connection to use, where the files reside and which fields will be used in the package.',
    editorDirective: '<cloud-storage-source-editor />',
    type: COMPONENT_CATEGORY.source,
    connectionTypes: `${ConnectionTypeName.s3},${ConnectionTypeName.gs},${ConnectionTypeName.hdfs},${ConnectionTypeName.sftp},${ConnectionTypeName.ftps},${ConnectionTypeName.azureBlobStorage}`,
    icon: 'icon-file-storage',
    componentType: COMPONENT_TYPE.CLOUD_STORAGE_SOURCE_COMPONENT,
    extraFields: [
      {
        name: 'file_path',
        type: 'string',
      },
    ],
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
    hasDataPreview: true,
    defaults: {
      record_delimiter: 'new_line',
      record_type: 'delimited',
      record_settings: {
        escape_character: '"',
        field_delimiter: ',',
        skip_header: true,
        text_qualifier: '',
      },
    },
  },
  {
    originalName: 'Database',
    editorDirective: '<database-source-editor />',
    type: COMPONENT_CATEGORY.source,
    connectionTypes: `${ConnectionTypeName.vertica},${ConnectionTypeName.azureSynapseAnalytics},${ConnectionTypeName.googleCloudSql},${ConnectionTypeName.googleCloudPostgres},${ConnectionTypeName.alloy},${ConnectionTypeName.herokuPostgres},${ConnectionTypeName.sqlserver},${ConnectionTypeName.postgres},${ConnectionTypeName.mysql},${ConnectionTypeName.oracle},${ConnectionTypeName.redshift},${ConnectionTypeName.snowflake},${ConnectionTypeName.athena},${ConnectionTypeName.db2},${ConnectionTypeName.as400}`,
    icon: 'icon-database',
    componentType: COMPONENT_TYPE.DATABASE_SOURCE_COMPONENT,
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
    hasDataPreview: true,
    defaults: {
      access_mode: 'table',
      parallel: 1,
      before_action: 'none',
    },
  },
  {
    originalName: 'Amazon Redshift',
    editorDirective: '<amazon-redshift-source-editor />',
    type: COMPONENT_CATEGORY.source,
    connectionTypes: ConnectionTypeName.redshift,
    icon: 'icon-amazon-redshift',
    componentType: COMPONENT_TYPE.AMAZON_REDSHIFT_SOURCE_COMPONENT,
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
    hasDataPreview: true,
    defaults: {
      access_mode: 'table',
    },
  },
  {
    originalName: 'Google BigQuery',
    editorDirective: '<big-query-source-editor />',
    type: COMPONENT_CATEGORY.source,
    icon: 'icon-big-query',
    connectionTypes: `${ConnectionTypeName.bigQuery},${ConnectionTypeName.bigQueryV2}`,
    componentType: COMPONENT_TYPE.BIG_QUERY_SOURCE_COMPONENT,
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
    defaults: {
      use_legacy_sql: true,
      stored_procedure: false,
      access_mode: 'table',
    },
    hasDataPreview: true,
  },
  {
    originalName: 'MongoDB',
    editorDirective: '<mongo-source-editor />',
    type: COMPONENT_CATEGORY.source,
    connectionTypes: ConnectionTypeName.mongo,
    icon: 'icon-mongo',
    componentType: COMPONENT_TYPE.MONGO_SOURCE_COMPONENT,
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
    defaults: {
      where_clause: '',
      access_mode: 'table',
    },
    hasDataPreview: true,
  },
  {
    originalName: 'Facebook Ads Insights',
    editorDirective: '<facebook-source-editor />',
    type: COMPONENT_CATEGORY.source,
    connectionTypes: ConnectionTypeName.facebookAdsInsights,
    disableSchemaLoad: true,
    icon: 'icon-facebook',
    componentType: COMPONENT_TYPE.FACEBOOK_ADS_INSIGHTS_SOURCE_COMPONENT,
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
    hasDataPreviewRefreshButton: true,
    defaults: {
      api_version: 'v15.0',
      level: 'account',
      report_date_range_type: 'last_7d',
      report_date_range_min: '',
      report_date_range_max: '',
      manual_breakdown: 'Default',
      time_increment: 'all_days',
      filtering: '',
      action_report_time: 'impression',
      report_on: 'all',
      ad_accounts: ['any'],
      with_custom_fields: false,
    },
    hasDataPreview: true,
  },
  {
    originalName: 'Google Ads',
    editorDirective: '<adwords-source-editor />',
    type: COMPONENT_CATEGORY.source,
    connectionTypes: ConnectionTypeName.adwords,
    icon: 'icon-google-adwords',
    componentType: COMPONENT_TYPE.ADWORDS_SOURCE_COMPONENT,
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
    disableSchemaLoad: true,
    hasDataPreview: false,
    defaults: {
      // api version default selected in designer service
      api_version: '',
      report_type: '',
      customer_ids: '',
      report_date_range_type: 'LAST_7_DAYS',
      report_date_range_min: '',
      report_date_range_max: '',
      customer_id_field_alias: 'CustomerID',
      report_on: 'all',
    },
  },
  {
    originalName: 'Google Analytics',
    editorDirective: '<analytics-source-editor />',
    type: COMPONENT_CATEGORY.source,
    connectionTypes: ConnectionTypeName.analytics,
    icon: 'icon-google-analytics',
    componentType: COMPONENT_TYPE.ANALYTICS_SOURCE_COMPONENT,
    disableSchemaLoad: true,
    hasDataPreview: true,
    hasDataPreviewRefreshButton: true,
    extraFields: [
      {
        name: 'file_path',
        type: 'string',
      },
    ],
    defaults: {
      profile_ids: '',
      report_date_range_type: 'CUSTOM_DATE',
      account_name_field_alias: 'account_name',
      property_name_field_alias: 'property_name',
      profile_name_field_alias: 'profile_name',
      profile_id_field_alias: 'profile_id',
      report_on: 'all',
      report_date_range_min: '7daysAgo',
      report_date_range_max: 'today',
      manual_breakdown: 'Default',
      segment: '',
      filters: null,
      sort: null,
      include_empty_rows: true,
      report_sampling_level: 'DEFAULT',
    },
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
  },
  {
    originalName: 'Google Analytics - GA4',
    editorDirective: '<analytics4-source-editor />',
    type: COMPONENT_CATEGORY.source,
    connectionTypes: ConnectionTypeName.analyticsV4,
    icon: 'icon-google-analytics',
    componentType: COMPONENT_TYPE.ANALYTICS_GA4_SOURCE_COMPONENT,
    disableSchemaLoad: true,
    hasDataPreview: true,
    hasDataPreviewRefreshButton: true,
    extraFields: [
      {
        name: 'file_path',
        type: 'string',
      },
    ],
    defaults: {
      profile_ids: '',
      report_date_range_type: 'CUSTOM_DATE',
      account_name_field_alias: 'account_name',
      property_name_field_alias: 'property_name',
      property_id_field_alias: 'property_id',
      account_id_field_alias: 'account_id',
      report_on: 'all',
      report_date_range_min: '7daysAgo',
      report_date_range_max: 'today',
      manual_breakdown: 'Default',
      segment: '',
      filters: null,
      sort: null,
      report_sampling_level: 'DEFAULT',
      api_version: 'v1beta',
    },
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
  },
  {
    originalName: 'REST API',
    editorDirective: '<rest-api-source-editor />',
    connectionTypes: `${ConnectionTypeName.analytics},${ConnectionTypeName.facebookAdsInsights},${ConnectionTypeName.facebookAds},${ConnectionTypeName.intercom},${ConnectionTypeName.xero},${ConnectionTypeName.linkedin},${ConnectionTypeName.instagram},${ConnectionTypeName.googleSheets},${ConnectionTypeName.googleDrive},${ConnectionTypeName.youtube},${ConnectionTypeName.shopify},${ConnectionTypeName.salesforce},${ConnectionTypeName.marketingCloud},${ConnectionTypeName.marketingCloudRest}`,
    type: COMPONENT_CATEGORY.source,
    icon: 'icon-rest-api',
    disableSchemaLoad: false,
    hasDataPreview: true,
    hasDataPreviewRefreshButton: true,
    componentType: COMPONENT_TYPE.REST_API_SOURCE_COMPONENT,
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
    defaults: {
      method: 'GET',
      response_type: 'json',
      pagination_scheme: 'auto',
      use_pagination: false,
      pagination_sleep_interval: 0,
      json_path: '$',
      authentication: AUTHENTICATION_TYPES.NONE,
    },
  },
  {
    originalName: 'Salesforce',
    editorDirective: '<salesforce-source-editor />',
    type: COMPONENT_CATEGORY.source,
    connectionTypes: ConnectionTypeName.salesforce,
    icon: 'icon-salesforce',
    componentType: COMPONENT_TYPE.SALESFORCE_SOURCE_COMPONENT,
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
    disableSchemaLoad: true,
    hasDataPreviewRefreshButton: true,
    defaults: {
      object_name: '',
      where_clause: '',
      source_action: 'query',
      access_mode: 'object',
      query: '',
      chunk_size: 100000,
    },
    hasDataPreview: true,
  },
  {
    originalName: 'Bing Ads',
    editorDirective: '<bing-ads-source-editor />',
    type: COMPONENT_CATEGORY.source,
    icon: 'icon-bing-ads',
    connectionTypes: ConnectionTypeName.bingAds,
    componentType: COMPONENT_TYPE.BING_ADS_SOURCE_COMPONENT,
    disableSchemaLoad: true,
    hasDataPreview: false,
    defaults: {
      api_version: 'v13',
      accounts_campaigns: '',
      report_type: '',
      columns: [],
      report_aggregation_type: 'SUMMARY',
      report_date_range_type: 'TODAY',
      report_date_range_min: '',
      report_date_range_max: '',
      time_zone: '',
      report_on: 'all',
    },
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
  },
  {
    originalName: 'NetSuite',
    editorDirective: '<netsuite-source-editor />',
    type: COMPONENT_CATEGORY.source,
    icon: 'icon-netsuite',
    connectionTypes: ConnectionTypeName.netsuite,
    componentType: COMPONENT_TYPE.NET_SUITE_SOURCE_COMPONENT,
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
    hasDataPreview: true,
    defaults: {
      access_mode: 'table',
    },
  },
  {
    originalName: 'Google Cloud Spanner',
    editorDirective: '<spanner-source-editor />',
    type: COMPONENT_CATEGORY.source,
    icon: 'icon-spanner',
    connectionTypes: ConnectionTypeName.googleCloudSpanner,
    componentType: COMPONENT_TYPE.SPANNER_SOURCE_COMPONENT,
    inputMin: 0,
    inputMax: 0,
    outputMin: 1,
    outputMax: 1,
    hasDataPreview: true,
  },
  {
    originalName: 'Select',
    editorDirective: '<select-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-select',
    componentType: COMPONENT_TYPE.SELECT_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 1,
    outputMax: 1,
  },
  {
    originalName: 'Sort',
    editorDirective: '<sort-editor-component />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-sort',
    componentType: COMPONENT_TYPE.SORT_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 1,
    outputMax: 1,
  },
  {
    originalName: 'Rank',
    editorDirective: '<rank-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-rank',
    componentType: COMPONENT_TYPE.RANK_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 1,
    outputMax: 1,
  },
  {
    originalName: 'Limit',
    editorDirective: '<limit-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-limit',
    componentType: COMPONENT_TYPE.LIMIT_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 1,
    outputMax: 1,
  },
  {
    originalName: 'Window',
    editorDirective: '<window-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-window',
    componentType: COMPONENT_TYPE.WINDOW_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 1,
    outputMax: 1,
    defaults: {
      partitioned_fields: [],
      ordered_fields: [],
      windowed_fields: [],
    },
  },
  {
    originalName: 'Sample',
    editorDirective: '<sample-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-sample',
    componentType: COMPONENT_TYPE.SAMPLE_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 1,
    outputMax: 1,
    defaults: {
      percentage: '',
    },
  },
  {
    originalName: 'Join',
    editorDirective: '<join-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-join',
    componentType: COMPONENT_TYPE.JOIN_COMPONENT,
    inputMin: 2,
    inputMax: 2,
    outputMin: 1,
    outputMax: 1,
    defaults: {
      join_type: 'inner',
      optimization_type: 'default',
      relations: [],
    },
  },
  {
    originalName: 'Cross join',
    editorDirective: '<cross-join-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-cross-join',
    componentType: COMPONENT_TYPE.CROSS_JOIN_COMPONENT,
    inputMin: 2,
    inputMax: 2,
    outputMin: 1,
    outputMax: 1,
  },
  {
    originalName: 'Clone',
    editorDirective: '<clone-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-clone',
    componentType: COMPONENT_TYPE.CLONE_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 1,
    outputMax: 10,
  },
  {
    originalName: 'Union',
    editorDirective: '<union-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-union',
    componentType: COMPONENT_TYPE.UNION_COMPONENT,
    inputMin: 2,
    inputMax: 2,
    outputMin: 1,
    outputMax: 1,
  },
  {
    originalName: 'Filter',
    editorDirective: '<filter-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-filter',
    componentType: COMPONENT_TYPE.FILTER_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 1,
    outputMax: 1,
    defaults: {
      predicates: {
        match_type: 'all',
        expressions: [],
      },
    },
  },
  {
    originalName: 'Assert',
    editorDirective: '<assert-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-assert',
    componentType: COMPONENT_TYPE.ASSERT_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 1,
    outputMax: 1,
    defaults: {
      message: 'Assertion failed',
      predicates: {
        match_type: 'all',
        expressions: [],
      },
    },
  },
  {
    originalName: 'Aggregate',
    editorDirective: '<aggregate-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-aggregate',
    componentType: COMPONENT_TYPE.AGGREGATE_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 1,
    outputMax: 1,
    defaults: {
      grouping_type: 'all',
      grouped_fields: [],
      aggregated_fields: [],
    },
  },
  {
    originalName: 'Cube',
    editorDirective: '<cube-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-cube',
    componentType: COMPONENT_TYPE.CUBE_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 1,
    outputMax: 1,
    defaults: {
      grouped_fields: [],
      aggregated_fields: [],
    },
  },
  {
    originalName: 'Distinct',
    editorDirective: '<distinct-editor />',
    type: COMPONENT_CATEGORY.transformation,
    icon: 'icon-distinct',
    componentType: COMPONENT_TYPE.DISTINCT_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 1,
    outputMax: 1,
  },
  {
    originalName: 'Amazon Redshift',
    editorDirective: '<amazon-redshift-destination-editor />',
    type: COMPONENT_CATEGORY.destination,
    connectionTypes: ConnectionTypeName.redshift,
    icon: 'icon-amazon-redshift',
    componentType: COMPONENT_TYPE.AMAZON_REDSHIFT_DESTINATION_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 0,
    outputMax: 0,
    defaults: {
      create_table: true,
      add_columns: true,
      operation_type: 'append',
      intermediate_compression_type: 'none',
      max_errors: 0,
      truncate_columns: true,
      trim_blanks: true,
      empty_as_null: false,
      blank_as_null: false,
      null_string: '\\N',
      replacement_char: '?',
      round_decimals: true,
      explicit_ids: false,
      comp_update: 'auto',
      comp_rows: 100000,
    },
  },
  {
    originalName: 'Google BigQuery',
    editorDirective: '<big-query-destination-editor />',
    type: COMPONENT_CATEGORY.destination,
    connectionTypes: `${ConnectionTypeName.bigQuery},${ConnectionTypeName.bigQueryV2}`,
    icon: 'icon-big-query',
    componentType: COMPONENT_TYPE.BIG_QUERY_DESTINATION_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 0,
    outputMax: 0,
    defaults: {
      max_bad_records: 0,
      create_table: true,
      add_columns: true,
      operation_type: 'append',
    },
  },
  {
    originalName: 'Database',
    editorDirective: '<database-destination-editor />',
    type: COMPONENT_CATEGORY.destination,
    icon: 'icon-database',
    connectionTypes: `${ConnectionTypeName.vertica},${ConnectionTypeName.azureSynapseAnalytics},${ConnectionTypeName.googleCloudSql},${ConnectionTypeName.googleCloudPostgres},${ConnectionTypeName.alloy},${ConnectionTypeName.herokuPostgres},${ConnectionTypeName.sqlserver},${ConnectionTypeName.postgres},${ConnectionTypeName.mysql},${ConnectionTypeName.oracle},${ConnectionTypeName.redshift}`,
    componentType: COMPONENT_TYPE.DATABASE_DESTINATION_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 0,
    outputMax: 0,
    defaults: {
      create_table: true,
      add_columns: true,
      operation_type: 'append',
      batch_size: 1000,
      parallel: 1,
      single_transaction_per_batch: false,
      identity_insert_sql_server: false,
    },
  },
  {
    originalName: 'MongoDB',
    editorDirective: '<mongo-destination-editor />',
    type: COMPONENT_CATEGORY.destination,
    icon: 'icon-mongo',
    connectionTypes: ConnectionTypeName.mongo,
    componentType: COMPONENT_TYPE.MONGO_DESTINATION_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 0,
    outputMax: 0,
    defaults: {
      operation_type: 'append',
    },
  },
  {
    originalName: 'File storage',
    editorDirective: '<cloud-storage-destination-editor />',
    type: COMPONENT_CATEGORY.destination,
    icon: 'icon-file-storage',
    connectionTypes: `${ConnectionTypeName.s3},${ConnectionTypeName.gs},${ConnectionTypeName.hdfs},${ConnectionTypeName.sftp},${ConnectionTypeName.ftps},${ConnectionTypeName.azureBlobStorage}`,
    componentType: COMPONENT_TYPE.CLOUD_STORAGE_DESTINATION_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 0,
    outputMax: 0,
    defaults: {
      bucket: '',
      path: '',
      file_names: 'default',
      prefix: '',
      suffix: null,
      destination_type: {
        csv_destination_type: {
          field_delimiter: ',',
          text_qualifier: '',
          write_header: true,
          escape_character: '"',
          line_ending: 'unix', // this sets initial value on ui
        },
      },
      compression_type: 'none',
      enforce_single_file: false,
      operation_type: 'fail_if_exists',
    },
  },
  {
    originalName: 'Google Spanner',
    editorDirective: '<spanner-destination-editor />',
    type: COMPONENT_CATEGORY.destination,
    icon: 'icon-spanner',
    connectionTypes: ConnectionTypeName.googleCloudSpanner,
    componentType: COMPONENT_TYPE.SPANNER_DESTINATION_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 0,
    outputMax: 0,
    defaults: {
      create_table: true,
      add_columns: true,
      operation_type: 'append',
    },
  },
  {
    originalName: 'Snowflake',
    editorDirective: '<snowflake-destination-editor />',
    type: COMPONENT_CATEGORY.destination,
    icon: 'icon-snowflake',
    connectionTypes: ConnectionTypeName.snowflake,
    componentType: COMPONENT_TYPE.SNOWFLAKE_DESTINATION_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 0,
    outputMax: 0,
    defaults: {
      warehouse_name: '',
      database_name: '',
      create_table: true,
      add_columns: true,
      operation_type: 'append',
      max_errors: 0,
      truncate_columns: true,
    },
  },
  {
    originalName: 'Salesforce',
    editorDirective: '<salesforce-destination-editor />',
    type: COMPONENT_CATEGORY.destination,
    icon: 'icon-salesforce',
    connectionTypes: ConnectionTypeName.salesforce,
    componentType: COMPONENT_TYPE.SALESFORCE_DESTINATION_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 0,
    outputMax: 0,
    defaults: {
      object_name: '',
      operation_type: 'insert',
      id_field: '',
      batch_size: 1000,
      parallel: 1,
      max_errors: 0,
      track_errors: false,
      errors_output_path: '',
      column_mappings: [],
    },
  },
  {
    originalName: 'Salesforce SOAP',
    editorDirective: '<salesforce-soap-destination-editor />',
    type: COMPONENT_CATEGORY.destination,
    icon: 'icon-salesforce',
    connectionTypes: ConnectionTypeName.salesforceSoap,
    componentType: COMPONENT_TYPE.SALESFORCE_SOAP_DESTINATION_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 0,
    outputMax: 0,
    defaults: {
      object_name: '',
      operation_type: 'insert',
      id_field: '',
      batch_size: 200,
      parallel: 1,
      max_errors: 0,
      track_errors: false,
      errors_output_path: '',
      column_mappings: [],
    },
  },
  {
    originalName: 'NetSuite SOAP',
    type: COMPONENT_CATEGORY.destination,
    editorDirective: '<netsuite-destination-editor />',
    icon: 'icon-netsuite',
    connectionTypes: ConnectionTypeName.netsuitesoap,
    componentType: COMPONENT_TYPE.NETSUITE_SOAP_DESTINATION_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 0,
    outputMax: 0,
    defaults: {
      object_name: '',
      operation_type: 'insert',
      id_field: '',
      batch_size: 100,
      max_errors: 0,
      column_mappings: [],
    },
  },
  {
    originalName: 'Facebook Ads',
    type: COMPONENT_CATEGORY.destination,
    editorDirective: '<facebook-destination-editor />',
    icon: 'icon-facebook',
    connectionTypes: 'facebookads',
    componentType: COMPONENT_TYPE.FACEBOOK_ADS_DESTINATION_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 0,
    outputMax: 0,
    defaults: {
      object_name: '',
      operation_type: 'insert',
      batch_size: 100,
      account_id: '',
      max_errors: 0,
      column_mappings: [],
    },
  },
  {
    originalName: 'Google Ads',
    type: COMPONENT_CATEGORY.destination,
    editorDirective: '<google-ads-destination-editor />',
    icon: 'icon-google-adwords',
    connectionTypes: ConnectionTypeName.adwords,
    componentType: COMPONENT_TYPE.GOOGLE_ADS_DESTINATION_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 0,
    outputMax: 0,
    defaults: {
      object_name: '',
      operation_type: 'insert',
      id_field: '',
      batch_size: 100,
      max_errors: 0,
      column_mappings: [],
    },
  },
  {
    originalName: 'TikTok Ads',
    type: COMPONENT_CATEGORY.destination,
    editorDirective: '<tiktok-ads-destination-editor />',
    icon: 'icon-tiktokads',
    connectionTypes: ConnectionTypeName.tiktokads,
    componentType: COMPONENT_TYPE.TIKTOK_ADS_DESTINATION_COMPONENT,
    inputMin: 1,
    inputMax: 1,
    outputMin: 0,
    outputMax: 0,
    defaults: {
      object_name: '',
      operation_type: 'insert',
      id_field: '',
      batch_size: 100,
      max_errors: 0,
      column_mappings: [],
    },
  },
  {
    originalName: 'Run package',
    editorDirective: '<run-package-editor />',
    type: COMPONENT_CATEGORY.workflow,
    icon: 'icon-run-package',
    componentType: COMPONENT_TYPE.RUN_PACKAGE_COMPONENT,
    inputMin: 1,
    inputMax: 100,
    outputMin: 1,
    outputMax: 100,
    defaults: {
      operator: 'all',
    },
  },
  {
    originalName: 'Execute SQL',
    editorDirective: '<execute-sql-editor />',
    type: COMPONENT_CATEGORY.workflow,
    icon: 'icon-execute-sql',
    connectionTypes: `${ConnectionTypeName.vertica},${ConnectionTypeName.azureSynapseAnalytics},${ConnectionTypeName.googleCloudPostgres},${ConnectionTypeName.alloy},${ConnectionTypeName.googleCloudSql},${ConnectionTypeName.bigQuery},${ConnectionTypeName.bigQueryV2},${ConnectionTypeName.redshift},${ConnectionTypeName.mysql},${ConnectionTypeName.herokuPostgres},${ConnectionTypeName.postgres},${ConnectionTypeName.sqlserver},${ConnectionTypeName.oracle},${ConnectionTypeName.snowflake},${ConnectionTypeName.athena},${ConnectionTypeName.db2},${ConnectionTypeName.as400}`,
    componentType: COMPONENT_TYPE.EXECUTE_SQL_COMPONENT,
    inputMin: 1,
    inputMax: 100,
    outputMin: 1,
    outputMax: 100,
    defaults: {
      operator: 'all',
    },
  },
];

export const COMPONENT_TYPES_SOURCES = COMPONENT_TYPES.filter(
  (component) => component.type === COMPONENT_CATEGORY.source,
);
export const COMPONENT_TYPES_TRANSFORMATIONS = COMPONENT_TYPES.filter(
  (component) => component.type === COMPONENT_CATEGORY.transformation,
);
export const COMPONENT_TYPES_DESTINATIONS = COMPONENT_TYPES.filter(
  (component) => component.type === COMPONENT_CATEGORY.destination,
);
export const COMPONENT_TYPES_WORKFLOWS = COMPONENT_TYPES.filter(
  (component) => component.type === COMPONENT_CATEGORY.workflow,
);
